import { useState, useEffect, useCallback } from 'react';
import {
  Wand2, CheckCircle2, Loader2, Copy, Check, AlertTriangle,
  TrendingUp, Users, ChevronRight,
} from 'lucide-react';
import { createStakeholder, getOpportunity, saveQualification, updateOpportunity } from '../../lib/mavisApi';
import type {
  ApplySuggestions,
  ApplySuggestionField,
  ForecastUpdateSuggestion,
  StakeholderSuggestion,
  NextActionSuggestion,
  MavisStakeholder,
  EngagementLevel,
  StakeholderRole,
} from '../../types/mavis';
import { MEDDPICC_KEY_LABELS, NOW_TO_VALUE_KEY_LABELS, STAKEHOLDER_ROLE_LABELS } from '../../types/mavis';

// ── Types ──────────────────────────────────────────────────────────────────────

type ApplyStatus = 'pending' | 'confirm' | 'applying' | 'applied' | 'ignored' | 'error';
type QualColumn = 'meddpicc' | 'now_to_value' | 'three_whys';

interface CurrentQual {
  meddpicc: Record<string, { value: string; notes: string; score: number }>;
  now_to_value: Record<string, { value: string; notes: string }>;
  three_whys: Record<string, string>;
}

const FORECAST_LABEL_DISPLAY: Record<string, string> = {
  low: 'Low', medium: 'Medium', high: 'High',
  commit_risk: 'Commit Risk', commit_ready: 'Commit Ready',
};

const THREE_WHYS_LABELS: Record<string, string> = {
  why_change: 'Why Change?',
  why_now:    'Why Now?',
  why_us:     'Why Us?',
};

function normalizeEngagement(level: string): EngagementLevel {
  if (level === 'medium') return 'moderate';
  if (['strong', 'moderate', 'weak', 'unknown'].includes(level)) return level as EngagementLevel;
  return 'unknown';
}

function normalizeRole(role: string): StakeholderRole {
  const valid: StakeholderRole[] = [
    'champion', 'economic_buyer', 'technical_buyer',
    'influencer', 'partner', 'detractor', 'blocker',
  ];
  return valid.includes(role as StakeholderRole) ? (role as StakeholderRole) : 'influencer';
}

// ── Shared sub-components ──────────────────────────────────────────────────────

function ConfidencePill({ confidence }: { confidence: number }) {
  const pct = Math.max(0, Math.min(100, confidence));
  const cls = pct >= 70 ? 'bg-forest-50 text-forest-700' : pct >= 40 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-600';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>
      {pct}%
    </span>
  );
}

function AppliedBadge({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-4 py-3 bg-forest-50 rounded-xl border border-forest-200">
      <CheckCircle2 className="w-4 h-4 text-forest-500 flex-shrink-0" />
      <span className="text-sm font-medium text-forest-700">{label} — Applied</span>
    </div>
  );
}

// ── Qualification field row ────────────────────────────────────────────────────

function QualFieldRow({
  label,
  suggestion,
  existingValue,
  status,
  errorMsg,
  onApply,
  onConfirmReplace,
  onCancelConfirm,
  onIgnore,
}: {
  label: string;
  suggestion: ApplySuggestionField;
  existingValue: string;
  status: ApplyStatus;
  errorMsg?: string;
  onApply: () => void;
  onConfirmReplace: () => void;
  onCancelConfirm: () => void;
  onIgnore: () => void;
}) {
  if (status === 'applied')  return <AppliedBadge label={label} />;
  if (status === 'ignored')  return null;

  const hasExisting = existingValue.trim().length > 0;

  return (
    <div className="p-4 bg-app-canvas rounded-xl border border-app-line">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-app-ink">{label}</span>
          {hasExisting && status !== 'confirm' && (
            <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-600 border border-amber-100">
              Will replace
            </span>
          )}
        </div>
        <ConfidencePill confidence={suggestion.confidence} />
      </div>

      <p className="text-xs text-app-ink leading-relaxed mb-1">{suggestion.suggested_value}</p>
      <p className="text-[11px] text-app-muted italic mb-3">{suggestion.reason}</p>

      {status === 'confirm' && (
        <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-[11px] font-semibold text-amber-700 mb-1">Current value:</p>
          <p className="text-[11px] text-amber-800 leading-relaxed line-clamp-3">{existingValue}</p>
        </div>
      )}

      {status === 'error' && errorMsg && (
        <p className="text-[11px] text-red-600 mb-2">{errorMsg}</p>
      )}

      <div className="flex items-center gap-2">
        {status === 'applying' ? (
          <Loader2 className="w-4 h-4 text-forest-500 animate-spin" />
        ) : status === 'confirm' ? (
          <>
            <button onClick={onConfirmReplace} className="px-3 py-1.5 text-xs font-medium text-app-surface bg-forest-600 hover:bg-forest-700 rounded-lg transition-colors">
              Replace existing
            </button>
            <button onClick={onCancelConfirm} className="px-3 py-1.5 text-xs font-medium text-app-muted hover:text-app-ink hover:bg-app-canvas rounded-lg transition-colors">
              Keep existing
            </button>
          </>
        ) : (
          <>
            <button onClick={onApply} className="px-3 py-1.5 text-xs font-medium text-app-surface bg-forest-600 hover:bg-forest-700 rounded-lg transition-colors">
              Apply
            </button>
            <button onClick={onIgnore} className="px-3 py-1.5 text-xs font-medium text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors">
              Ignore
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Props ──────────────────────────────────────────────────────────────────────

interface Props {
  opportunityId: string;
  orgId: string;
  accountId: string;
  suggestions: ApplySuggestions;
  onForecastApplied?: (patch: Record<string, unknown>) => void;
  onStakeholderAdded?: (s: MavisStakeholder) => void;
  onQualificationApplied?: (column: QualColumn, merged: Record<string, unknown>) => void;
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function MavisApplySuggestionsPanel({
  opportunityId,
  accountId,
  suggestions,
  onForecastApplied,
  onStakeholderAdded,
  onQualificationApplied,
}: Props) {
  const [statuses,  setStatuses]  = useState<Record<string, ApplyStatus>>({});
  const [errorMsgs, setErrorMsgs] = useState<Record<string, string>>({});
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  const [currentQual,     setCurrentQual]     = useState<CurrentQual | null>(null);
  const [currentForecast, setCurrentForecast] = useState<{ forecast_label: string | null; forecast_percentage: number | null } | null>(null);
  const [existingStakeholderKeys, setExistingStakeholderKeys] = useState<Set<string>>(new Set());
  const [contextLoading,  setContextLoading]  = useState(true);

  // ── Load current DB state on mount ──────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const detail = await getOpportunity(opportunityId);

        if (cancelled) return;

        if (detail.qualification) {
          setCurrentQual({
            meddpicc:     (detail.qualification.meddpicc     ?? {}) as CurrentQual['meddpicc'],
            now_to_value: (detail.qualification.now_to_value ?? {}) as CurrentQual['now_to_value'],
            three_whys:   (detail.qualification.three_whys   ?? {}) as CurrentQual['three_whys'],
          });
        }
        setCurrentForecast({
          forecast_label:      detail.opportunity.forecast_label,
          forecast_percentage: detail.opportunity.forecast_percentage,
        });
        setExistingStakeholderKeys(
          new Set(detail.stakeholders.map(s => `${s.name.toLowerCase()}::${s.role_in_deal}`)),
        );
      } catch { /* non-fatal */ } finally {
        if (!cancelled) setContextLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [opportunityId]);

  // ── State helpers ────────────────────────────────────────────────────────────

  const setStatus = useCallback((key: string, status: ApplyStatus) =>
    setStatuses(prev => ({ ...prev, [key]: status })), []);

  const setErrMsg = useCallback((key: string, msg: string) =>
    setErrorMsgs(prev => ({ ...prev, [key]: msg })), []);

  const getStatus = (key: string): ApplyStatus => statuses[key] ?? 'pending';

  // ── Qualification apply ──────────────────────────────────────────────────────

  const getExistingQualValue = (column: QualColumn, fieldKey: string): string => {
    if (!currentQual) return '';
    if (column === 'three_whys') return currentQual.three_whys[fieldKey] ?? '';
    if (column === 'meddpicc')   return currentQual.meddpicc[fieldKey]?.value ?? '';
    return currentQual.now_to_value[fieldKey]?.value ?? '';
  };

  const performQualApply = useCallback(async (key: string, column: QualColumn, fieldKey: string, value: string) => {
    setStatus(key, 'applying');
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const freshData = (await getOpportunity(opportunityId)).qualification as any;
      let merged: Record<string, unknown>;
      if (column === 'three_whys') {
        const current = (freshData?.[column] as Record<string, string>) ?? {};
        merged = { ...current, [fieldKey]: value };
      } else {
        const defaultField = column === 'meddpicc'
          ? { value: '', notes: '', score: 0 }
          : { value: '', notes: '' };
        const current = (freshData?.[column] as Record<string, Record<string, unknown>>) ?? {};
        merged = { ...current, [fieldKey]: { ...(current[fieldKey] ?? defaultField), value } };
      }

      await saveQualification(opportunityId, { [column]: merged });
      setStatus(key, 'applied');
      // Update local qual context so subsequent rows reflect the new value
      setCurrentQual(prev => {
        if (!prev) return prev;
        if (column === 'three_whys') return { ...prev, three_whys: merged as CurrentQual['three_whys'] };
        if (column === 'meddpicc')   return { ...prev, meddpicc:   merged as CurrentQual['meddpicc'] };
        return { ...prev, now_to_value: merged as CurrentQual['now_to_value'] };
      });
      onQualificationApplied?.(column, merged);
    } catch (err) {
      setErrMsg(key, err instanceof Error ? err.message : 'Failed to apply');
      setStatus(key, 'error');
    }
  }, [opportunityId, setStatus, setErrMsg, onQualificationApplied]);

  const handleQualApply = (column: QualColumn, fieldKey: string, suggestion: ApplySuggestionField) => {
    const key = `${column}__${fieldKey}`;
    const existing = getExistingQualValue(column, fieldKey);
    if (existing.trim()) {
      setStatus(key, 'confirm');
    } else {
      performQualApply(key, column, fieldKey, suggestion.suggested_value);
    }
  };

  // ── Forecast apply ───────────────────────────────────────────────────────────

  const performForecastApply = useCallback(async (suggestion: ForecastUpdateSuggestion) => {
    setStatus('forecast', 'applying');
    try {
      const patch = {
        forecast_label:      suggestion.forecast_label,
        forecast_percentage: suggestion.forecast_percentage,
      };
      await updateOpportunity(opportunityId, patch);
      setStatus('forecast', 'applied');
      onForecastApplied?.(patch);
    } catch (err) {
      setErrMsg('forecast', err instanceof Error ? err.message : 'Failed to apply');
      setStatus('forecast', 'error');
    }
  }, [opportunityId, onForecastApplied, setStatus, setErrMsg]);

  // ── Stakeholder apply ────────────────────────────────────────────────────────

  const performStakeholderAdd = useCallback(async (key: string, idx: number, suggestion: StakeholderSuggestion) => {
    setStatus(key, 'applying');
    try {
      const role = normalizeRole(suggestion.role_in_deal);
      const engagement = normalizeEngagement(suggestion.engagement_level);

      const { stakeholder: inserted } = await createStakeholder(opportunityId, {
        name:            suggestion.name,
        title:           suggestion.title ?? '',
        role_in_deal:    role,
        engagement_level: engagement,
        notes:           suggestion.reason ?? '',
        contact_id:      null,
      });

      setStatus(key, 'applied');
      setExistingStakeholderKeys(prev => new Set([...prev, `${suggestion.name.toLowerCase()}::${role}`]));
      onStakeholderAdded?.(inserted as unknown as MavisStakeholder);
    } catch (err) {
      setErrMsg(key, err instanceof Error ? err.message : 'Failed to add stakeholder');
      setStatus(key, 'error');
    }
  }, [opportunityId, onStakeholderAdded, setStatus, setErrMsg]);

  // ── Copy helpers ─────────────────────────────────────────────────────────────

  const copyAction = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const copyAllActions = async (actions: NextActionSuggestion[]) => {
    const text = actions
      .map((a, i) => `${i + 1}. ${a.action}\n   ${a.rationale}${a.suggested_timing ? ` (${a.suggested_timing})` : ''}`)
      .join('\n\n');
    try {
      await navigator.clipboard.writeText(text);
      setCopiedAll(true);
      setTimeout(() => setCopiedAll(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  const qualUpdates = suggestions.qualification_updates;
  const meddpiccSugs = qualUpdates?.meddpicc ?? {};
  const ntvSugs      = qualUpdates?.now_to_value ?? {};
  const whysSugs     = qualUpdates?.three_whys ?? {};
  const forecastSug  = suggestions.forecast_update;
  const stakeholders = suggestions.stakeholder_suggestions ?? [];
  const nextActions  = suggestions.next_actions ?? [];

  const hasQualSugs =
    Object.keys(meddpiccSugs).length > 0 ||
    Object.keys(ntvSugs).length > 0 ||
    Object.keys(whysSugs).length > 0;

  if (contextLoading) {
    return (
      <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm px-5 py-6 flex items-center gap-3">
        <Loader2 className="w-4 h-4 text-app-muted animate-spin flex-shrink-0" />
        <p className="text-sm text-app-muted">Loading apply context…</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Section header */}
      <div className="flex items-center gap-2 pt-2">
        <Wand2 className="w-4 h-4 text-forest-500" />
        <h3 className="text-sm font-semibold text-app-ink">Apply Suggestions</h3>
        <span className="text-xs text-app-muted">— MAVIS recommends the following updates to your workspace</span>
      </div>

      {/* ── Qualification updates ── */}
      {hasQualSugs && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-app-muted" />
            <h4 className="text-sm font-semibold text-app-ink">Qualification Updates</h4>
          </div>
          <div className="px-5 py-4 space-y-5">

            {/* MEDDPICC */}
            {Object.keys(meddpiccSugs).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-app-muted uppercase tracking-wide mb-3">MEDDPICC</p>
                <div className="space-y-3">
                  {(Object.entries(meddpiccSugs) as [keyof typeof meddpiccSugs, ApplySuggestionField][]).map(([fieldKey, sug]) => {
                    const key = `meddpicc__${fieldKey}`;
                    const status = getStatus(key);
                    return (
                      <QualFieldRow
                        key={fieldKey}
                        label={MEDDPICC_KEY_LABELS[fieldKey as keyof typeof MEDDPICC_KEY_LABELS] ?? fieldKey}
                        suggestion={sug}
                        existingValue={getExistingQualValue('meddpicc', fieldKey)}
                        status={status}
                        errorMsg={errorMsgs[key]}
                        onApply={() => handleQualApply('meddpicc', fieldKey, sug)}
                        onConfirmReplace={() => performQualApply(key, 'meddpicc', fieldKey, sug.suggested_value)}
                        onCancelConfirm={() => setStatus(key, 'pending')}
                        onIgnore={() => setStatus(key, 'ignored')}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* Now to Value */}
            {Object.keys(ntvSugs).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-app-muted uppercase tracking-wide mb-3">Now to Value</p>
                <div className="space-y-3">
                  {(Object.entries(ntvSugs) as [keyof typeof ntvSugs, ApplySuggestionField][]).map(([fieldKey, sug]) => {
                    const key = `now_to_value__${fieldKey}`;
                    const status = getStatus(key);
                    return (
                      <QualFieldRow
                        key={fieldKey}
                        label={NOW_TO_VALUE_KEY_LABELS[fieldKey as keyof typeof NOW_TO_VALUE_KEY_LABELS] ?? fieldKey}
                        suggestion={sug}
                        existingValue={getExistingQualValue('now_to_value', fieldKey)}
                        status={status}
                        errorMsg={errorMsgs[key]}
                        onApply={() => handleQualApply('now_to_value', fieldKey, sug)}
                        onConfirmReplace={() => performQualApply(key, 'now_to_value', fieldKey, sug.suggested_value)}
                        onCancelConfirm={() => setStatus(key, 'pending')}
                        onIgnore={() => setStatus(key, 'ignored')}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {/* 3 Whys */}
            {Object.keys(whysSugs).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-app-muted uppercase tracking-wide mb-3">3 Whys</p>
                <div className="space-y-3">
                  {(Object.entries(whysSugs) as [keyof typeof whysSugs, ApplySuggestionField][]).map(([fieldKey, sug]) => {
                    const key = `three_whys__${fieldKey}`;
                    const status = getStatus(key);
                    return (
                      <QualFieldRow
                        key={fieldKey}
                        label={THREE_WHYS_LABELS[fieldKey] ?? fieldKey}
                        suggestion={sug}
                        existingValue={getExistingQualValue('three_whys', fieldKey)}
                        status={status}
                        errorMsg={errorMsgs[key]}
                        onApply={() => handleQualApply('three_whys', fieldKey, sug)}
                        onConfirmReplace={() => performQualApply(key, 'three_whys', fieldKey, sug.suggested_value)}
                        onCancelConfirm={() => setStatus(key, 'pending')}
                        onIgnore={() => setStatus(key, 'ignored')}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Forecast update ── */}
      {forecastSug && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-app-muted" />
            <h4 className="text-sm font-semibold text-app-ink">Forecast Update</h4>
          </div>
          <div className="px-5 py-4">
            {getStatus('forecast') === 'applied' ? (
              <AppliedBadge label="Forecast" />
            ) : getStatus('forecast') === 'ignored' ? null : (
              <div className="p-4 bg-app-canvas rounded-xl border border-app-line">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="text-xs font-semibold text-app-ink mb-0.5">
                      Suggested: <span className="text-forest-700">{FORECAST_LABEL_DISPLAY[forecastSug.forecast_label] ?? forecastSug.forecast_label}</span>
                      {' '}
                      <span className="text-forest-700">{forecastSug.forecast_percentage}%</span>
                    </p>
                    {currentForecast?.forecast_label && (
                      <p className="text-[11px] text-app-muted">
                        Current: {FORECAST_LABEL_DISPLAY[currentForecast.forecast_label] ?? currentForecast.forecast_label}
                        {currentForecast.forecast_percentage != null ? ` ${currentForecast.forecast_percentage}%` : ''}
                      </p>
                    )}
                  </div>
                  <ConfidencePill confidence={forecastSug.confidence} />
                </div>

                <p className="text-xs text-app-muted leading-relaxed mb-3">{forecastSug.reason}</p>

                {getStatus('forecast') === 'confirm' && (
                  <div className="mb-3 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                    <p className="text-[11px] text-amber-700">
                      This will replace the current forecast. Proceed?
                    </p>
                  </div>
                )}

                {errorMsgs['forecast'] && (
                  <p className="text-[11px] text-red-600 mb-2">{errorMsgs['forecast']}</p>
                )}

                <div className="flex items-center gap-2">
                  {getStatus('forecast') === 'applying' ? (
                    <Loader2 className="w-4 h-4 text-forest-500 animate-spin" />
                  ) : getStatus('forecast') === 'confirm' ? (
                    <>
                      <button
                        onClick={() => performForecastApply(forecastSug)}
                        className="px-3 py-1.5 text-xs font-medium text-app-surface bg-forest-600 hover:bg-forest-700 rounded-lg transition-colors"
                      >
                        Apply forecast
                      </button>
                      <button
                        onClick={() => setStatus('forecast', 'pending')}
                        className="px-3 py-1.5 text-xs font-medium text-app-muted hover:text-app-ink hover:bg-app-canvas rounded-lg transition-colors"
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => {
                          const sameLabel = currentForecast?.forecast_label === forecastSug.forecast_label;
                          const samePct   = currentForecast?.forecast_percentage === forecastSug.forecast_percentage;
                          if (currentForecast?.forecast_label && !(sameLabel && samePct)) {
                            setStatus('forecast', 'confirm');
                          } else {
                            performForecastApply(forecastSug);
                          }
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-app-surface bg-forest-600 hover:bg-forest-700 rounded-lg transition-colors"
                      >
                        Apply
                      </button>
                      <button
                        onClick={() => setStatus('forecast', 'ignored')}
                        className="px-3 py-1.5 text-xs font-medium text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
                      >
                        Ignore
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Stakeholder suggestions ── */}
      {stakeholders.length > 0 && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center gap-2">
            <Users className="w-4 h-4 text-app-muted" />
            <h4 className="text-sm font-semibold text-app-ink">Suggested Stakeholders</h4>
          </div>
          <div className="px-5 py-4 space-y-3">
            {stakeholders.map((sug, idx) => {
              const key = `stakeholder__${idx}`;
              const role = normalizeRole(sug.role_in_deal);
              const isDuplicate = existingStakeholderKeys.has(`${sug.name.toLowerCase()}::${role}`);
              const status = getStatus(key);

              if (status === 'ignored') return null;
              if (status === 'applied') {
                return <AppliedBadge key={idx} label={sug.name} />;
              }

              return (
                <div key={idx} className="p-4 bg-app-canvas rounded-xl border border-app-line">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div>
                      <p className="text-xs font-semibold text-app-ink">{sug.name}</p>
                      {sug.title && <p className="text-[11px] text-app-muted">{sug.title}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {STAKEHOLDER_ROLE_LABELS[role]}
                      </span>
                      <ConfidencePill confidence={sug.confidence} />
                    </div>
                  </div>

                  <p className="text-xs text-app-muted leading-relaxed mb-3">{sug.reason}</p>

                  {isDuplicate && (
                    <div className="flex items-center gap-2 mb-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                      <p className="text-[11px] text-amber-700">A stakeholder with this name and role already exists.</p>
                    </div>
                  )}

                  {errorMsgs[key] && (
                    <p className="text-[11px] text-red-600 mb-2">{errorMsgs[key]}</p>
                  )}

                  <div className="flex items-center gap-2">
                    {status === 'applying' ? (
                      <Loader2 className="w-4 h-4 text-forest-500 animate-spin" />
                    ) : (
                      <>
                        <button
                          onClick={() => performStakeholderAdd(key, idx, sug)}
                          disabled={isDuplicate}
                          className="px-3 py-1.5 text-xs font-medium text-app-surface bg-forest-600 hover:bg-forest-700 disabled:opacity-40 disabled:cursor-not-allowed rounded-lg transition-colors"
                        >
                          Add to team
                        </button>
                        <button
                          onClick={() => setStatus(key, 'ignored')}
                          className="px-3 py-1.5 text-xs font-medium text-app-muted hover:text-app-muted hover:bg-app-canvas rounded-lg transition-colors"
                        >
                          Ignore
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Next actions ── */}
      {nextActions.length > 0 && (
        <div className="bg-app-surface rounded-2xl border border-app-line shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-app-line flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChevronRight className="w-4 h-4 text-app-muted" />
              <h4 className="text-sm font-semibold text-app-ink">Next Actions</h4>
            </div>
            <button
              onClick={() => copyAllActions(nextActions)}
              className="flex items-center gap-1 text-[11px] font-medium text-app-muted hover:text-forest-600 transition-colors"
            >
              {copiedAll ? (
                <><Check className="w-3 h-3 text-forest-500" /><span className="text-forest-600">Copied all</span></>
              ) : (
                <><Copy className="w-3 h-3" />Copy all</>
              )}
            </button>
          </div>
          <div className="px-5 py-4 space-y-3">
            {nextActions.map((action, idx) => {
              const key = `action__${idx}`;
              const PRIORITY_CLS: Record<string, string> = {
                high:   'bg-red-50 text-red-700 border-red-100',
                medium: 'bg-amber-50 text-amber-700 border-amber-100',
                low:    'bg-app-canvas text-app-muted border-app-line',
              };
              const priCls = PRIORITY_CLS[action.priority?.toLowerCase()] ?? PRIORITY_CLS.low;
              const isCopied = copiedKey === key;

              return (
                <div key={idx} className="flex gap-3 p-3 bg-app-canvas rounded-xl border border-app-line">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-xs font-semibold text-app-ink">{action.action}</p>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${priCls}`}>
                        {(action.priority ?? 'medium').charAt(0).toUpperCase() + (action.priority ?? 'medium').slice(1)}
                      </span>
                    </div>
                    <p className="text-[11px] text-app-muted leading-relaxed mb-1">{action.rationale}</p>
                    {(action.suggested_owner || action.suggested_timing) && (
                      <p className="text-[10px] text-app-muted">
                        {action.suggested_owner && <span>Owner: {action.suggested_owner}</span>}
                        {action.suggested_owner && action.suggested_timing && <span> · </span>}
                        {action.suggested_timing && <span>{action.suggested_timing}</span>}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => copyAction(key, `${action.action}\n${action.rationale}`)}
                    className="flex-shrink-0 flex items-center gap-1 text-[11px] font-medium text-app-muted hover:text-forest-600 transition-colors self-start pt-0.5"
                  >
                    {isCopied ? (
                      <><Check className="w-3 h-3 text-forest-500" /></>
                    ) : (
                      <><Copy className="w-3 h-3" /></>
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
